import React, { useEffect, useRef, useState } from 'react'
import HintContent from './HintContent'
import MaskedAnswer from './MaskedAnswer'

export default function VocabularyPrompt({
  vocabulary,
  totalScore,
  targetScore,
  onSubmit,
  onUseHint,
  onNext,
  submitting
}) {
  const [answer, setAnswer] = useState('')
  const [feedback, setFeedback] = useState(null) // { correct: bool, message: string }
  const [revealed, setRevealed] = useState(null)
  const [hintUsed, setHintUsed] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [])

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault()
    if (submitting || !answer.trim()) return

    try {
      const result = await onSubmit(answer)
      if (result.correct) {
        setFeedback({ correct: true, message: 'Chính xác! 🎉' })
      } else {
        setFeedback({ correct: false, message: 'Chưa đúng, hãy thử lại.' })
      }
    } catch (err) {
      setFeedback({ correct: false, message: 'Có lỗi khi gửi đáp án. Thử lại nhé.' })
    }
  }

  const handleHint = async () => {
    if (submitting) return
    try {
      const result = await onUseHint()
      if (result?.answer) setRevealed(result.answer)
      setHintUsed(true)
    } catch (err) {
      setFeedback({ correct: false, message: 'Không thể lấy gợi ý.' })
    }
  }

  const done = feedback?.correct || hintUsed
  const disabled = submitting || done
  // Hide the "Bạn trả lời" badge once the kid types more than 3 chars so
  // the label doesn't hover over the typing view for long answers.
  const showInputBadge = answer.length <= 3
  // "Câu N/T" is the mastery counter: N is the word you're currently
  // working on toward mastery. Since re-queued words don't advance the
  // counter, this matches the kid's mental model.
  const attemptNumber = Math.min(totalScore + 1, targetScore)

  return (
    <div className="vocabulary-card">
      <div className="vocabulary-progress">
        <span>Câu {attemptNumber} / {targetScore}</span>
        <span>·</span>
        <span>Điểm: <strong>{totalScore}</strong> / {targetScore}</span>
      </div>

      <HintContent hint={vocabulary.hint} />

      <div className="vocabulary-bar-wrapper">
        <div className="vocabulary-masked" aria-label="answer preview">
          <MaskedAnswer
            masked={vocabulary.masked_answer}
            answer={vocabulary.answer || ''}
            input={answer}
          />
        </div>
        <span className="vocabulary-badge hint-badge" data-testid="vocabulary-hint-badge">
          Gợi ý
        </span>
      </div>

      {revealed && (
        <div className="vocabulary-hint-reveal">Đáp án: {revealed}</div>
      )}

      {!hintUsed && (
        <form
          onSubmit={handleSubmit}
          className={`vocabulary-bar-wrapper${showInputBadge ? ' with-badge' : ''}`}
        >
          <input
            ref={inputRef}
            type="text"
            className="vocabulary-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={disabled}
            data-testid="vocabulary-answer-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {showInputBadge && (
            <span
              className="vocabulary-badge answer-badge"
              data-testid="vocabulary-answer-badge"
            >
              Bạn trả lời
            </span>
          )}
        </form>
      )}

      {feedback && (
        <div className={`vocabulary-feedback ${feedback.correct ? 'success' : 'error'}`}>
          {feedback.message}
        </div>
      )}

      <div className="vocabulary-actions">
        {!hintUsed && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleHint}
            disabled={submitting || feedback?.correct}
            data-testid="vocabulary-hint-button"
          >
            💡 Xem đáp án
          </button>
        )}
        {done ? (
          <button
            type="button"
            className="btn btn-primary ms-auto"
            onClick={onNext}
            data-testid="vocabulary-next-button"
          >
            Tiếp theo →
          </button>
        ) : (
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={disabled || !answer.trim()}
            data-testid="vocabulary-submit-button"
          >
            Kiểm tra
          </button>
        )}
      </div>
    </div>
  )
}
